/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Directive, EventEmitter, Inject, Optional, Self, forwardRef} from '@angular/core';

import {AbstractControl, FormControl, FormGroup} from '../model';
import {NG_ASYNC_VALIDATORS, NG_VALIDATORS} from '../validators';

import {ControlContainer} from './control_container';
import {Form} from './form_interface';
import {NgControl} from './ng_control';
import {NgModel} from './ng_model';
import {NgModelGroup} from './ng_model_group';
import {composeAsyncValidators, composeValidators, setUpControl, setUpFormContainer} from './shared';

export const /** @type {?} */ formDirectiveProvider: any = {
  provide: ControlContainer,
  useExisting: forwardRef(() => NgForm)
};

const /** @type {?} */ resolvedPromise = Promise.resolve(null);
/**
 * \@whatItDoes Creates a top-level {\@link FormGroup} instance and binds it to a form
 * to track aggregate form value and validation status.
 * 
 * \@howToUse 
 * 
 * As soon as you import the `FormsModule`, this directive becomes active by default on
 * all `<form>` tags.  You don't need to add a special selector.
 * 
 * You can export the directive into a local template variable using `ngForm` as the key
 * (ex: `#myForm="ngForm"`). This is optional, but useful.  Many properties from the underlying
 * {\@link FormGroup} instance are duplicated on the directive itself, so a reference to it
 * will give you access to the aggregate value and validity status of the form, as well as
 * user interaction properties like `dirty` and `touched`.
 * 
 * To register child controls with the form, you'll want to use {\@link NgModel} with a
 * `name` attribute.  You can also use {\@link NgModelGroup} if you'd like to create
 * sub-groups within the form.
 * 
 * You can listen to the directive's `ngSubmit` event to be notified when the user has
 * triggered a form submission. The `ngSubmit` event will be emitted with the original form
 * submission event.
 * 
 * {\@example forms/ts/simpleForm/simple_form_example.ts region='Component'}
 * 
 * * **npm package**: `\@angular/forms`
 * 
 * * **NgModule**: `FormsModule`
 * 
 *  \@stable
 */
export class NgForm extends ControlContainer implements Form {
private _submitted: boolean = false;

  form: FormGroup;
  ngSubmit = new EventEmitter();
/**
 * @param {?} validators
 * @param {?} asyncValidators
 */
constructor(
         validators: any[],
         asyncValidators: any[]) {
    super();
    this.form =
        new FormGroup({}, composeValidators(validators), composeAsyncValidators(asyncValidators));
  }
/**
 * @return {?}
 */
get submitted(): boolean { return this._submitted; }
/**
 * @return {?}
 */
get formDirective(): Form { return this; }
/**
 * @return {?}
 */
get control(): FormGroup { return this.form; }
/**
 * @return {?}
 */
get path(): string[] { return []; }
/**
 * @return {?}
 */
get controls(): {[key: string]: AbstractControl} { return this.form.controls; }
/**
 * @param {?} dir
 * @return {?}
 */
addControl(dir: NgModel): void {
    resolvedPromise.then(() => {
      const /** @type {?} */ container = this._findContainer(dir.path);
      dir._control = /** @type {?} */(( <FormControl>container.registerControl(dir.name, dir.control)));
      setUpControl(dir.control, dir);
      dir.control.updateValueAndValidity({emitEvent: false});
    });
  }
/**
 * @param {?} dir
 * @return {?}
 */
getControl(dir: NgModel): FormControl { return /** @type {?} */(( <FormControl>this.form.get(dir.path))); }
/**
 * @param {?} dir
 * @return {?}
 */
removeControl(dir: NgModel): void {
    resolvedPromise.then(() => {
      const /** @type {?} */ container = this._findContainer(dir.path);
      if (container) {
        container.removeControl(dir.name);
      }
    });
  }
/**
 * @param {?} dir
 * @return {?}
 */
addFormGroup(dir: NgModelGroup): void {
    resolvedPromise.then(() => {
      const /** @type {?} */ container = this._findContainer(dir.path);
      const /** @type {?} */ group = new FormGroup({});
      setUpFormContainer(group, dir);
      container.registerControl(dir.name, group);
      group.updateValueAndValidity({emitEvent: false});
    });
  }
/**
 * @param {?} dir
 * @return {?}
 */
removeFormGroup(dir: NgModelGroup): void {
    resolvedPromise.then(() => {
      const /** @type {?} */ container = this._findContainer(dir.path);
      if (container) {
        container.removeControl(dir.name);
      }
    });
  }
/**
 * @param {?} dir
 * @return {?}
 */
getFormGroup(dir: NgModelGroup): FormGroup { return /** @type {?} */(( <FormGroup>this.form.get(dir.path))); }
/**
 * @param {?} dir
 * @param {?} value
 * @return {?}
 */
updateModel(dir: NgControl, value: any): void {
    resolvedPromise.then(() => {
      const /** @type {?} */ ctrl = /** @type {?} */(( <FormControl>this.form.get( /** @type {?} */((dir.path)))));
      ctrl.setValue(value);
    });
  }
/**
 * @param {?} value
 * @return {?}
 */
setValue(value: {[key: string]: any}): void { this.control.setValue(value); }
/**
 * @param {?} $event
 * @return {?}
 */
onSubmit($event: Event): boolean {
    this._submitted = true;
    this.ngSubmit.emit($event);
    return false;
  }
/**
 * @return {?}
 */
onReset(): void { this.resetForm(); }
/**
 * @param {?=} value
 * @return {?}
 */
resetForm(value: any = undefined): void {
    this.form.reset(value);
    this._submitted = false;
  }
/**
 * \@internal
 * @param {?} path
 * @return {?}
 */
_findContainer(path: string[]): FormGroup {
    path.pop();
    return path.length ? /** @type {?} */(( <FormGroup>this.form.get(path))) : this.form;
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'form:not([ngNoForm]):not([formGroup]),ngForm,[ngForm]',
  providers: [formDirectiveProvider],
  host: {'(submit)': 'onSubmit($event)', '(reset)': 'onReset()'},
  outputs: ['ngSubmit'],
  exportAs: 'ngForm'
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Array, decorators: [{ type: Optional }, { type: Self }, { type: Inject, args: [NG_VALIDATORS, ] }, ]},
{type: Array, decorators: [{ type: Optional }, { type: Self }, { type: Inject, args: [NG_ASYNC_VALIDATORS, ] }, ]},
];
}

function NgForm_tsickle_Closure_declarations() {
/** @type {?} */
NgForm.decorators;
/**
 * @nocollapse
 * @type {?}
 */
NgForm.ctorParameters;
/** @type {?} */
NgForm.prototype._submitted;
/** @type {?} */
NgForm.prototype.form;
/** @type {?} */
NgForm.prototype.ngSubmit;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
